import { FONTS } from "@/domain/options";
import type {
  BackgroundOption,
  FontId,
  IconOption,
  TextColorOption,
  ThemeOption,
} from "@/domain/types";
import type { AppLocale } from "@/i18n/routing";
import { logServer } from "@/integrations/posthog/log-server";
import { getAppSupabaseClient } from "@/integrations/supabase/client";

const DESIGN_ASSETS_BUCKET = "design-assets";
const CATALOG_TTL_MS = 60_000;
const RESOLVER_TTL_MS = 60_000;
const ASSET_BYTES_LRU_MAX = 32;

export interface DesignAssetCatalog {
  backgrounds: BackgroundOption[];
  icons: IconOption[];
}

export type DesignCategoryKind = "icon" | "background" | "text_color";

export interface DesignCategory {
  id: string;
  kind: DesignCategoryKind;
  label: string;
  sortOrder: number;
}

export class DesignAssetNotFoundError extends Error {
  constructor(assetKey: string, cause?: unknown) {
    super(`Design asset not found in bucket "${DESIGN_ASSETS_BUCKET}" at "${assetKey}".`);
    this.name = "DesignAssetNotFoundError";
    if (cause !== undefined) {
      (this as Error & { cause?: unknown }).cause = cause;
    }
  }
}

// DB row shapes, mirroring task 069's migration columns.
interface DesignBackgroundRow {
  id: string;
  enabled: boolean;
  sort_order: number;
  label_sv: string;
  label_en: string;
  kind: "solid" | "image";
  hex: string | null;
  preview_url: string | null;
  source_asset_key: string | null;
  dark_text: boolean;
  category_ids: string[];
  search_terms: string[];
}

interface DesignIconRow {
  id: string;
  enabled: boolean;
  sort_order: number;
  label_sv: string;
  label_en: string;
  asset_key: string;
  preview_asset_key: string | null;
  category_ids: string[];
  search_terms: string[];
}

interface DesignTextColorRow {
  id: string;
  enabled: boolean;
  sort_order: number;
  label_sv: string;
  label_en: string;
  hex: string;
  category_ids: string[];
  search_terms: string[];
}

interface DesignCategoryRow {
  id: string;
  kind: DesignCategoryKind;
  label_sv: string;
  label_en: string;
  sort_order: number;
  enabled: boolean;
}

interface DesignThemeRow {
  id: string;
  enabled: boolean;
  sort_order: number;
  label_sv: string;
  label_en: string;
  caption_sv: string;
  caption_en: string;
  background_id: string;
  text_color_id: string;
  font_id: string;
  icon_id: string | null;
  tags: string[];
}

interface TestOverrides {
  fetchBackgroundRows?: () => Promise<DesignBackgroundRow[]> | DesignBackgroundRow[];
  fetchIconRows?: () => Promise<DesignIconRow[]> | DesignIconRow[];
  fetchTextColorRows?: () => Promise<DesignTextColorRow[]> | DesignTextColorRow[];
  fetchThemeRows?: () => Promise<DesignThemeRow[]> | DesignThemeRow[];
  fetchCategoryRows?: () => Promise<DesignCategoryRow[]> | DesignCategoryRow[];
  resolveBackgroundRow?: (
    id: string
  ) => Promise<DesignBackgroundRow | null> | DesignBackgroundRow | null;
  resolveIconRow?: (id: string) => Promise<DesignIconRow | null> | DesignIconRow | null;
  resolveTextColorRow?: (
    id: string
  ) => Promise<DesignTextColorRow | null> | DesignTextColorRow | null;
  downloadAssetBytes?: (assetKey: string) => Promise<Uint8Array | null> | Uint8Array | null;
}

let testOverrides: TestOverrides = {};

interface CatalogCacheEntry {
  value: DesignAssetCatalog;
  expiresAt: number;
}

interface BackgroundCacheEntry {
  value: BackgroundOption | null;
  expiresAt: number;
}

interface IconCacheEntry {
  value: IconOption | null;
  expiresAt: number;
}

interface TextColorCatalogCacheEntry {
  value: TextColorOption[];
  expiresAt: number;
}

interface TextColorCacheEntry {
  value: TextColorOption | null;
  expiresAt: number;
}

interface ThemeCatalogCacheEntry {
  value: ThemeOption[];
  expiresAt: number;
}

interface CategoryCatalogCacheEntry {
  value: DesignCategory[];
  expiresAt: number;
}

// In-process caches. Last-writer-wins on stampede; acceptable at v1 traffic.
const catalogCache = new Map<AppLocale, CatalogCacheEntry>();
const backgroundByIdCache = new Map<string, BackgroundCacheEntry>();
const iconByIdCache = new Map<string, IconCacheEntry>();
const textColorCatalogCache = new Map<AppLocale, TextColorCatalogCacheEntry>();
const textColorByIdCache = new Map<string, TextColorCacheEntry>();
const themeCatalogCache = new Map<AppLocale, ThemeCatalogCacheEntry>();
// Category catalog cache key: `${kind}:${locale}` — categories are partitioned
// by kind, so each (kind, locale) pair caches independently.
const categoryCatalogCache = new Map<string, CategoryCatalogCacheEntry>();

// Simple LRU: reinsert on read to make the latest read the most-recent entry.
// `null` results are never cached — transient errors should not poison bytes.
const assetBytesLru = new Map<string, Uint8Array>();

function now(): number {
  return Date.now();
}

// Color-wheel sort for the picker. Achromatic (gray/black/white) rows lead,
// dark → light; chromatic rows follow, ordered by HSL hue. Within the same
// bucket, sort_order acts as the operator-controlled tiebreaker. Rows without
// a hex (image backgrounds) sort to the very end. The "lead with neutrals"
// choice keeps `ink` near the top of the picker — it's the default text
// color and yanking it to the tail would surprise users.
// 0.10 (not 0.08) so `gray` (#6B7280, s ≈ 0.089) groups with the neutrals
// instead of sorting as a near-blue.
const ACHROMATIC_SATURATION_THRESHOLD = 0.1;

interface HueSortKey {
  group: 0 | 1 | 2; // 0 = achromatic, 1 = chromatic, 2 = no-hex / unknown
  primary: number; // lightness for group 0, hue for group 1
  saturation: number;
  lightness: number;
}

function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  const normalized = hex.replace(/^#/, "").toLowerCase();
  if (!/^[0-9a-f]{6}$/.test(normalized)) return null;
  const r = parseInt(normalized.slice(0, 2), 16) / 255;
  const g = parseInt(normalized.slice(2, 4), 16) / 255;
  const b = parseInt(normalized.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return { h: h * 360, s, l };
}

function getHueSortKey(hex: string | null): HueSortKey {
  if (hex === null) return { group: 2, primary: 0, saturation: 0, lightness: 0 };
  const hsl = hexToHsl(hex);
  if (hsl === null) return { group: 2, primary: 0, saturation: 0, lightness: 0 };
  if (hsl.s < ACHROMATIC_SATURATION_THRESHOLD) {
    return { group: 0, primary: hsl.l, saturation: hsl.s, lightness: hsl.l };
  }
  return { group: 1, primary: hsl.h, saturation: hsl.s, lightness: hsl.l };
}

function compareByHue<T extends { hex: string | null; sort_order: number }>(a: T, b: T): number {
  const ka = getHueSortKey(a.hex);
  const kb = getHueSortKey(b.hex);
  if (ka.group !== kb.group) return ka.group - kb.group;
  if (ka.group === 0) {
    if (ka.lightness !== kb.lightness) return ka.lightness - kb.lightness;
    return a.sort_order - b.sort_order;
  }
  if (ka.group === 1) {
    if (ka.primary !== kb.primary) return ka.primary - kb.primary;
    if (ka.saturation !== kb.saturation) return kb.saturation - ka.saturation;
    if (ka.lightness !== kb.lightness) return ka.lightness - kb.lightness;
    return a.sort_order - b.sort_order;
  }
  return a.sort_order - b.sort_order;
}

function isFresh<T extends { expiresAt: number }>(entry: T | undefined): entry is T {
  return entry !== undefined && entry.expiresAt > now();
}

function pickLabel(row: { label_sv: string; label_en: string }, locale: AppLocale): string {
  return locale === "sv" ? row.label_sv : row.label_en;
}

function normalizePreviewUrl(previewUrl: string): string {
  if (previewUrl.startsWith("/")) {
    const base = process.env.SUPABASE_URL?.trim().replace(/\/+$/, "") ?? "";
    return `${base}${previewUrl}`;
  }
  return previewUrl;
}

function buildPublicAssetUrl(assetKey: string): string {
  const base = process.env.SUPABASE_URL?.trim().replace(/\/+$/, "") ?? "";
  return `${base}/storage/v1/object/public/${DESIGN_ASSETS_BUCKET}/${assetKey}`;
}

function mapBackgroundRow(row: DesignBackgroundRow, locale: AppLocale): BackgroundOption {
  const label = pickLabel(row, locale);

  if (row.kind === "solid") {
    if (row.hex === null) {
      // Row-level check in the migration makes this unreachable; treat as a contract drift.
      throw new Error(`Design background "${row.id}" is marked solid but has no hex.`);
    }
    return {
      id: row.id,
      label,
      preview: { kind: "solid", hex: row.hex },
      source: { kind: "solid", hex: row.hex },
      darkText: row.dark_text,
      categoryIds: row.category_ids,
      searchTerms: row.search_terms,
    };
  }

  if (row.preview_url === null || row.source_asset_key === null) {
    throw new Error(
      `Design background "${row.id}" is marked image but missing preview_url or source_asset_key.`
    );
  }

  return {
    id: row.id,
    label,
    preview: { kind: "image", url: normalizePreviewUrl(row.preview_url) },
    source: { kind: "asset", assetKey: row.source_asset_key },
    darkText: row.dark_text,
    categoryIds: row.category_ids,
    searchTerms: row.search_terms,
  };
}

function mapIconRow(row: DesignIconRow, locale: AppLocale): IconOption {
  // The configurator UI fetches `previewUrl` (small WebP variant when present);
  // the print path keeps using `assetKey` to fetch full-resolution source bytes.
  const previewKey = row.preview_asset_key ?? row.asset_key;
  return {
    id: row.id,
    label: pickLabel(row, locale),
    assetKey: row.asset_key,
    previewUrl: buildPublicAssetUrl(previewKey),
    categoryIds: row.category_ids,
    searchTerms: row.search_terms,
  };
}

function mapTextColorRow(row: DesignTextColorRow, locale: AppLocale): TextColorOption {
  return {
    id: row.id,
    label: pickLabel(row, locale),
    hex: row.hex,
    categoryIds: row.category_ids,
    searchTerms: row.search_terms,
  };
}

function mapCategoryRow(row: DesignCategoryRow, locale: AppLocale): DesignCategory {
  return {
    id: row.id,
    kind: row.kind,
    label: pickLabel(row, locale),
    sortOrder: row.sort_order,
  };
}

async function fetchBackgroundRows(): Promise<DesignBackgroundRow[]> {
  if (testOverrides.fetchBackgroundRows) {
    return await testOverrides.fetchBackgroundRows();
  }

  const client = getAppSupabaseClient();
  const { data, error } = await client
    .from("design_backgrounds")
    .select(
      "id, enabled, sort_order, label_sv, label_en, kind, hex, preview_url, source_asset_key, dark_text, category_ids, search_terms"
    )
    .order("sort_order", { ascending: true });

  if (error) {
    throw error;
  }

  return (data ?? []) as DesignBackgroundRow[];
}

async function fetchIconRows(): Promise<DesignIconRow[]> {
  if (testOverrides.fetchIconRows) {
    return await testOverrides.fetchIconRows();
  }

  const client = getAppSupabaseClient();
  const { data, error } = await client
    .from("design_icons")
    .select(
      "id, enabled, sort_order, label_sv, label_en, asset_key, preview_asset_key, category_ids, search_terms"
    )
    .order("sort_order", { ascending: true });

  if (error) {
    throw error;
  }

  return (data ?? []) as DesignIconRow[];
}

async function resolveBackgroundRow(id: string): Promise<DesignBackgroundRow | null> {
  if (testOverrides.resolveBackgroundRow) {
    return await testOverrides.resolveBackgroundRow(id);
  }

  const client = getAppSupabaseClient();
  const { data, error } = await client
    .from("design_backgrounds")
    .select(
      "id, enabled, sort_order, label_sv, label_en, kind, hex, preview_url, source_asset_key, dark_text, category_ids, search_terms"
    )
    .eq("id", id)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return (data as DesignBackgroundRow | null) ?? null;
}

async function resolveIconRow(id: string): Promise<DesignIconRow | null> {
  if (testOverrides.resolveIconRow) {
    return await testOverrides.resolveIconRow(id);
  }

  const client = getAppSupabaseClient();
  const { data, error } = await client
    .from("design_icons")
    .select(
      "id, enabled, sort_order, label_sv, label_en, asset_key, preview_asset_key, category_ids, search_terms"
    )
    .eq("id", id)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return (data as DesignIconRow | null) ?? null;
}

async function fetchTextColorRows(): Promise<DesignTextColorRow[]> {
  if (testOverrides.fetchTextColorRows) {
    return await testOverrides.fetchTextColorRows();
  }

  const client = getAppSupabaseClient();
  const { data, error } = await client
    .from("design_text_colors")
    .select("id, enabled, sort_order, label_sv, label_en, hex, category_ids, search_terms")
    .order("sort_order", { ascending: true });

  if (error) {
    throw error;
  }

  return (data ?? []) as DesignTextColorRow[];
}

async function resolveTextColorRow(id: string): Promise<DesignTextColorRow | null> {
  if (testOverrides.resolveTextColorRow) {
    return await testOverrides.resolveTextColorRow(id);
  }

  const client = getAppSupabaseClient();
  const { data, error } = await client
    .from("design_text_colors")
    .select("id, enabled, sort_order, label_sv, label_en, hex, category_ids, search_terms")
    .eq("id", id)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return (data as DesignTextColorRow | null) ?? null;
}

async function fetchCategoryRows(): Promise<DesignCategoryRow[]> {
  if (testOverrides.fetchCategoryRows) {
    return await testOverrides.fetchCategoryRows();
  }

  const client = getAppSupabaseClient();
  const { data, error } = await client
    .from("design_categories")
    .select("id, kind, label_sv, label_en, sort_order, enabled")
    .order("sort_order", { ascending: true });

  if (error) {
    // PGRST205 = "table not found in schema cache". Lets the code ship before
    // the design_categories migration is applied without crashing the picker.
    if ((error as { code?: string }).code === "PGRST205") {
      // Chip rail renders empty until the design_categories migration is applied.
      logServer("warn", "design_categories_table_missing", { code: "PGRST205" });
      return [];
    }
    throw error;
  }

  return (data ?? []) as DesignCategoryRow[];
}

async function fetchThemeRows(): Promise<DesignThemeRow[]> {
  if (testOverrides.fetchThemeRows) {
    return await testOverrides.fetchThemeRows();
  }

  const client = getAppSupabaseClient();
  const { data, error } = await client
    .from("design_themes")
    .select(
      "id, enabled, sort_order, label_sv, label_en, caption_sv, caption_en, background_id, text_color_id, font_id, icon_id, tags"
    )
    .order("sort_order", { ascending: true });

  if (error) {
    // PGRST205 = "table not found in schema cache". Lets the code ship before
    // the design_themes migration is applied without crashing the homepage.
    if ((error as { code?: string }).code === "PGRST205") {
      // Themes section is skipped until the design_themes migration is applied.
      logServer("warn", "design_themes_table_missing", { code: "PGRST205" });
      return [];
    }
    throw error;
  }

  return (data ?? []) as DesignThemeRow[];
}

async function downloadAssetBytes(assetKey: string): Promise<Uint8Array | null> {
  if (testOverrides.downloadAssetBytes) {
    const result = await testOverrides.downloadAssetBytes(assetKey);
    return result ?? null;
  }

  const client = getAppSupabaseClient();
  const { data, error } = await client.storage.from(DESIGN_ASSETS_BUCKET).download(assetKey);

  if (error) {
    // Supabase returns an error for missing objects — treat as "not found" at the adapter boundary.
    throw new DesignAssetNotFoundError(assetKey, error);
  }

  if (!data) {
    return null;
  }

  const arrayBuffer = await data.arrayBuffer();
  return new Uint8Array(arrayBuffer);
}

function lruTouch(key: string, bytes: Uint8Array): void {
  if (assetBytesLru.has(key)) {
    assetBytesLru.delete(key);
  } else if (assetBytesLru.size >= ASSET_BYTES_LRU_MAX) {
    const oldest = assetBytesLru.keys().next().value;
    if (oldest !== undefined) {
      assetBytesLru.delete(oldest);
    }
  }
  assetBytesLru.set(key, bytes);
}

function lruRead(key: string): Uint8Array | undefined {
  const bytes = assetBytesLru.get(key);
  if (bytes === undefined) {
    return undefined;
  }
  // Re-insert to mark most-recently-used.
  assetBytesLru.delete(key);
  assetBytesLru.set(key, bytes);
  return bytes;
}

export async function getDesignAssetCatalog(locale: AppLocale): Promise<DesignAssetCatalog> {
  const cached = catalogCache.get(locale);
  if (isFresh(cached)) {
    return cached.value;
  }

  const [backgroundRows, iconRows] = await Promise.all([fetchBackgroundRows(), fetchIconRows()]);

  const bySortOrder = <T extends { sort_order: number }>(a: T, b: T): number =>
    a.sort_order - b.sort_order;

  const catalog: DesignAssetCatalog = {
    backgrounds: [...backgroundRows]
      .sort(compareByHue)
      .filter((row) => row.enabled)
      .map((row) => mapBackgroundRow(row, locale)),
    icons: [...iconRows]
      .sort(bySortOrder)
      .filter((row) => row.enabled)
      .map((row) => mapIconRow(row, locale)),
  };

  catalogCache.set(locale, { value: catalog, expiresAt: now() + CATALOG_TTL_MS });
  return catalog;
}

// Ignores `enabled` so disabled rows still resolve for historic orders. `null`
// results are cached for the TTL window to prevent a malformed-id reconstruction
// loop from hammering Supabase; no permanent negative cache.
export async function resolveBackgroundById(
  id: string,
  locale: AppLocale
): Promise<BackgroundOption | null> {
  const cacheKey = `${locale}:${id}`;
  const cached = backgroundByIdCache.get(cacheKey);
  if (isFresh(cached)) {
    return cached.value;
  }

  const row = await resolveBackgroundRow(id);
  const value = row === null ? null : mapBackgroundRow(row, locale);

  backgroundByIdCache.set(cacheKey, { value, expiresAt: now() + RESOLVER_TTL_MS });
  return value;
}

export async function resolveIconById(id: string, locale: AppLocale): Promise<IconOption | null> {
  const cacheKey = `${locale}:${id}`;
  const cached = iconByIdCache.get(cacheKey);
  if (isFresh(cached)) {
    return cached.value;
  }

  const row = await resolveIconRow(id);
  const value = row === null ? null : mapIconRow(row, locale);

  iconByIdCache.set(cacheKey, { value, expiresAt: now() + RESOLVER_TTL_MS });
  return value;
}

export async function getDesignTextColorCatalog(locale: AppLocale): Promise<TextColorOption[]> {
  const cached = textColorCatalogCache.get(locale);
  if (isFresh(cached)) {
    return cached.value;
  }

  const rows = await fetchTextColorRows();

  const catalog = [...rows]
    .sort(compareByHue)
    .filter((row) => row.enabled)
    .map((row) => mapTextColorRow(row, locale));

  textColorCatalogCache.set(locale, { value: catalog, expiresAt: now() + CATALOG_TTL_MS });
  return catalog;
}

// Ignores `enabled` so disabled rows still resolve for historic orders. `null`
// results are cached for the TTL window to prevent a malformed-id reconstruction
// loop from hammering Supabase; no permanent negative cache.
export async function resolveTextColorById(
  id: string,
  locale: AppLocale
): Promise<TextColorOption | null> {
  const cacheKey = `${locale}:${id}`;
  const cached = textColorByIdCache.get(cacheKey);
  if (isFresh(cached)) {
    return cached.value;
  }

  const row = await resolveTextColorRow(id);
  const value = row === null ? null : mapTextColorRow(row, locale);

  textColorByIdCache.set(cacheKey, { value, expiresAt: now() + RESOLVER_TTL_MS });
  return value;
}

/**
 * Featured-theme catalog (task 146). Resolves each theme row against the
 * enabled-filtered design-asset catalogs:
 * - referenced background or text color missing/disabled → drop the theme
 * - referenced icon missing/disabled → drop the icon, keep the theme
 * - unknown font id → drop the theme
 *
 * Cached per locale for the same TTL as the underlying catalogs.
 */
export async function getDesignThemes(locale: AppLocale): Promise<ThemeOption[]> {
  const cached = themeCatalogCache.get(locale);
  if (isFresh(cached)) {
    return cached.value;
  }

  const [themeRows, assetCatalog, textColors] = await Promise.all([
    fetchThemeRows(),
    getDesignAssetCatalog(locale),
    getDesignTextColorCatalog(locale),
  ]);

  const backgroundById = new Map(assetCatalog.backgrounds.map((b) => [b.id, b]));
  const iconById = new Map(assetCatalog.icons.map((i) => [i.id, i]));
  const textColorById = new Map(textColors.map((c) => [c.id, c]));
  const fontIds = new Set<FontId>(FONTS.map((f) => f.id));

  const themes: ThemeOption[] = [];
  for (const row of [...themeRows].sort((a, b) => a.sort_order - b.sort_order)) {
    if (!row.enabled) continue;

    const background = backgroundById.get(row.background_id);
    if (!background) {
      logServer("warn", "design_theme_dropped", {
        themeId: row.id,
        reason: "background_missing_or_disabled",
        backgroundId: row.background_id,
      });
      continue;
    }

    const textColor = textColorById.get(row.text_color_id);
    if (!textColor) {
      logServer("warn", "design_theme_dropped", {
        themeId: row.id,
        reason: "text_color_missing_or_disabled",
        textColorId: row.text_color_id,
      });
      continue;
    }

    if (!fontIds.has(row.font_id)) {
      logServer("warn", "design_theme_dropped", {
        themeId: row.id,
        reason: "font_unknown",
        fontId: row.font_id,
      });
      continue;
    }

    let icon: IconOption | null = null;
    if (row.icon_id !== null) {
      const resolved = iconById.get(row.icon_id);
      if (resolved) {
        icon = resolved;
      } else {
        // Theme is kept — it just renders without its icon.
        logServer("warn", "design_theme_icon_missing", {
          themeId: row.id,
          iconId: row.icon_id,
        });
      }
    }

    themes.push({
      id: row.id,
      label: pickLabel(row, locale),
      caption: locale === "sv" ? row.caption_sv : row.caption_en,
      background,
      textColor,
      icon,
      font: row.font_id,
      tags: row.tags,
    });
  }

  themeCatalogCache.set(locale, { value: themes, expiresAt: now() + CATALOG_TTL_MS });
  return themes;
}

/**
 * Returns the chip-rail categories for one picker kind, localized and sorted
 * by sort_order. The kind partitioning lives in `design_categories.kind`, so
 * `getDesignCategories("icon", ...)` never leaks background or text-color
 * categories — see the migration's comment for the enforcement boundary.
 */
export async function getDesignCategories(
  kind: DesignCategoryKind,
  locale: AppLocale
): Promise<DesignCategory[]> {
  const cacheKey = `${kind}:${locale}`;
  const cached = categoryCatalogCache.get(cacheKey);
  if (isFresh(cached)) {
    return cached.value;
  }

  const rows = await fetchCategoryRows();

  const bySortOrder = <T extends { sort_order: number }>(a: T, b: T): number =>
    a.sort_order - b.sort_order;

  const categories = [...rows]
    .filter((row) => row.enabled && row.kind === kind)
    .sort(bySortOrder)
    .map((row) => mapCategoryRow(row, locale));

  categoryCatalogCache.set(cacheKey, {
    value: categories,
    expiresAt: now() + CATALOG_TTL_MS,
  });
  return categories;
}

export async function fetchDesignAssetBytes(assetKey: string): Promise<Uint8Array> {
  const cached = lruRead(assetKey);
  if (cached) {
    return cached;
  }

  const bytes = await downloadAssetBytes(assetKey);
  if (!bytes) {
    throw new DesignAssetNotFoundError(assetKey);
  }

  lruTouch(assetKey, bytes);
  return bytes;
}

export function __setDesignAssetCatalogTestOverrides(overrides: TestOverrides): void {
  testOverrides = overrides;
  catalogCache.clear();
  backgroundByIdCache.clear();
  iconByIdCache.clear();
  textColorCatalogCache.clear();
  textColorByIdCache.clear();
  themeCatalogCache.clear();
  categoryCatalogCache.clear();
  assetBytesLru.clear();
}

export function __resetDesignAssetCatalogTestOverrides(): void {
  testOverrides = {};
  catalogCache.clear();
  backgroundByIdCache.clear();
  iconByIdCache.clear();
  textColorCatalogCache.clear();
  textColorByIdCache.clear();
  themeCatalogCache.clear();
  categoryCatalogCache.clear();
  assetBytesLru.clear();
}
